/**
 * Shared help items for the application
 * Centralizes help documentation to ensure consistency across views
 */

// Common help items that are used in multiple places
export const commonHelpItems = {
    // Language indicators help
    languageIndicators: {
        title: 'Details',
        description: `Shows available languages for this video with color-coded status indicators:
      • Green (Published): Final version ready for distribution
      • Purple (Level 3): Reviewed by senior editor
      • Deep Purple (Level 2): Reviewed by editor
      • Blue (Level 1): Initial edit completed
      • Orange (AI Generated): Raw AI output, needs editing
      For a folder it shows the number of videos or folders available within it.`,
        position: 'bottom',
    },

    // Assignment chips help
    assignments: {
        title: 'Assignment Indicators',
        description: `(Only in the assignment toggle view) Video assignments show who is responsible for editing specific languages:
      • Solid chips: Assigned to you
      • Outlined chips: Assigned to others
      • Level indicator shows assignment category (Level 1-3)
      • Status indicators: ✓ (Completed), ⟳ (In Progress), ⚠️ (Overdue)
      Click on your assignments to update their status.`,
        position: 'bottom',
    },

    // File actions help
    fileActions: {
        title: 'Actions Menu',
        description: `Access video management options:
      • Rename: Change the video name or folder name
      • Move to Folder: Move the video or a folder to a different location       
      • Delete: Remove the video or a folder
      • Assign: Assign a language from a video to work on by team members, your assignments show up in the assignment view, with the count in the chip count
      • Manage Progress Stages: View and update the workflow stages of the video (Between AI, Level 1 - 3, Published)
      • View Version History: See the edit versions history of languages associated with the video, You can restore any version from the version history. 
      • Regenerate: Regnerate the AI subtitles for the video. This will overwrite any existing AI subtitles.
      • Text 2 Text Translations: Translate the subtitles of the video from a source language to a target language(s).`,
        position: 'left',
    },

    // Header language chips help
    headerLanguageChips: {
        title: 'Language Access Chips',
        description: `Shows your access levels to different languages:
      • Green: ADMIN access - full control to edit and manage
      • Blue: WRITE access - can edit subtitles
      • Orange: READ access - can view but not edit
      Click any chip to see all your language permissions.`,
        position: 'bottom',
    },

    // Header menu help
    headerMenu: {
        title: 'User Menu',
        description: `Access account and system options:
      • Active Sessions: Manage open editing sessions
      • Help: Access application guidance
      • System Status: View system status
      • Video Processing Status: View video processing status
      • Report a bug: Report a bug, ensure to include the video ID and a description of the issue and the logs
      • Dark/Light Mode: Toggle display theme
      • Logout: End your current session`,
        position: 'bottom',
    },

    backtoDashboard: {
        title: 'Back to Dashboard',
        description: `Click here to return to the main dashboard view.`,
        position: 'left',
    },
};

// List view specific help items
export const userViewHelpItems = [
    {
        selector: '.header-language-chips',
        ...commonHelpItems.headerLanguageChips,
    },
    {
        selector: '.header-user-menu',
        ...commonHelpItems.headerMenu,
    },
    {
        selector: '.header-theme-toggle',
        title: 'Theme Toggle',
        description: 'Switch between light and dark display modes.',
        position: 'bottom',
    },
    {
        selector: '.breadcrumbs',
        title: 'Breadcrumbs',
        description: 'Shows the current folder path. Click to navigate to a parent folder.',
        position: 'bottom',
    },
    {
        selector: '.search-field',
        title: 'Search Field',
        description:
            'Search videos by name, language, and process stages. You can search in the folder or across all folders.',
        position: 'bottom',
    },
    {
        selector: '.sort-selector',
        title: 'Sort Selector',
        description: 'Change how files are ordered (by name, date, etc.).',
        position: 'bottom',
    },
    {
        selector: '.view-toggle',
        title: 'Toggle between Grid and List View',
        description: 'Switch between Grid View and List View.',
        position: 'left',
    },
    {
        selector: '.view-assignement-button',
        title: 'View Assignments Button',
        description: 'Switch between All items and Assigned Items.',
        position: 'left',
    },
    {
        selector: '.refresh-button',
        title: 'Refresh Button',
        description: 'Refresh the current view to see the latest updates.',
        position: 'left',
    },
    {
        selector: '.create-folder-button',
        title: 'Create Folder Button',
        description: 'Create a new folder to organize your videos.',
        position: 'left',
    },
    {
        selector: '.upload-video-button',
        title: 'Upload Video Button',
        description: 'Upload a new video file to create subtitles. Supports MP4, MKV and other common video formats.',
        position: 'left',
    },
    {
        selector: '.file-folder-item',
        title: 'Video File or Folder',
        description:
            'Click to open this video/file for editing or the navigate in the folder. The row shows key information about the video/folder file.',
        position: 'left',
    },
    {
        selector: '.file-folder-name',
        title: 'File Name',
        description:
            'The name of the video file or the folder. For a video, below it you can see the video ID when available.',
        position: 'left',
    },
    {
        selector: '.folder-file-details',
        ...commonHelpItems.languageIndicators,
    },

    {
        selector: '.file-folder-duration',
        title: 'Video Duration',
        description:
            'The duration of the video file. This is often descriptive of the video content, or - for a folder',
        position: 'bottom',
    },
    {
        selector: '.file-folder-updated-by',
        title: 'Last Updated By',
        description: 'Shows who last modified this file.',
        position: 'bottom',
    },
    {
        selector: '.file-folder-updated-at',
        title: 'Last Updated At',
        description: 'Shows when this file was last modified.',
        position: 'bottom',
    },
    {
        selector: '.file-folder-action-menu-button',
        ...commonHelpItems.fileActions,
    },

    {
        selector: '.list-file-assignments',
        ...commonHelpItems.assignments,
    },
];

/**
 * Help items for administrative functions
 * These items provide guidance for user management, assignment management, and bug reports
 */
export const adminViewHelpItems = [
    // User Management
    {
        selector: '.user-management-section',
        title: 'User Management',
        description: `Manage all system users and their permissions:
      • View all registered users in the system
      • Create new user accounts with specific roles
      • Modify existing user permissions and language access
      • Reset passwords for users who need assistance
      • Deactivate accounts that are no longer needed`,
        position: 'right',
    },
    {
        selector: '.user-list',
        title: 'User List',
        description: `View and manage all system users:
      • Search for users by name or email
      • Click on any user to view their full profile
      • Sort users by name, role, or last activity date
      • Quick indicators show access levels and active status`,
        position: 'bottom',
    },
    {
        selector: '.user-create-button',
        title: 'Create New User',
        description: `Add a new user to the system:
      • Enter user's email address and basic information
      • Assign role (Admin, Editor, or Viewer)
      • Set language access permissions for each language
      • Set password or generate a random secure password
      • User will receive an email with login instructions`,
        position: 'left',
    },
    {
        selector: '.user-permissions-section',
        title: 'Language Permissions',
        description: `Manage language access for users:
      • READ: User can only view subtitles for this language
      • WRITE: User can edit subtitles for this language
      • ADMIN: User has full control over content in this language
      • Changes to permissions take effect immediately
      • Users can have different access levels for different languages`,
        position: 'bottom',
    },
    {
        selector: '.user-actions-menu',
        title: 'User Actions',
        description: `Perform administrative actions on user accounts:
      • Edit Profile: Update user information
      • Reset Password: Send password reset link
      • Modify Permissions: Change role or language access
      • Lock Account: Temporarily prevent login
      • Delete: Permanently remove user (use with caution)`,
        position: 'left',
    },

    // Assignment Management
    {
        selector: '.assignments-management-section',
        title: 'Assignment Management',
        description: `Oversee all subtitle editing assignments in the system:
      • View all active assignments across users and languages
      • Create new assignments for specific videos and languages
      • Track progress and completion status
      • Set and adjust deadlines
      • Reassign tasks when necessary`,
        position: 'right',
    },
    {
        selector: '.assignment-filters',
        title: 'Assignment Filters',
        description: `Filter the assignments view:
      • By User: See all tasks assigned to a specific user
      • By Language: View assignments for specific languages
      • By Status: Filter by Not Started, In Progress, or Completed
      • By Due Date: Find overdue assignments or upcoming deadlines
      • By Priority: Focus on high-priority tasks first`,
        position: 'bottom',
    },
    {
        selector: '.create-assignment-form',
        title: 'Create Assignment',
        description: `Assign subtitle editing work to users:
      • Select video from the repository
      • Choose target language for subtitling
      • Select appropriate user based on language skills
      • Set processing level (1-3) and instructions
      • Specify due date and priority
      • Add optional notes with specific guidance`,
        position: 'left',
    },
    {
        selector: '.batch-assignment-tool',
        title: 'Batch Assignment',
        description: `Create multiple assignments at once:
      • Upload CSV file with assignment details
      • Or select multiple videos and assign to same user
      • Set common parameters for all assignments
      • Review before confirming
      • Users will be notified of new assignments automatically`,
        position: 'bottom',
    },
    {
        selector: '.assignment-progress-chart',
        title: 'Progress Overview',
        description: `Monitor assignment completion status:
      • Visual representation of assignment progress
      • Track completion percentages by language
      • Identify bottlenecks in the workflow
      • See upcoming deadlines and at-risk assignments
      • Export reports for offline analysis`,
        position: 'left',
    },

    // Bug Reports
    {
        selector: '.bug-reports-section',
        title: 'Bug Reports',
        description: `Review and manage reported issues:
      • View all user-reported problems
      • Sort by date, status, or severity
      • Assign bugs to team members for resolution
      • Track resolution progress
      • Communicate updates to users who reported issues`,
        position: 'right',
    },
    {
        selector: '.bug-list',
        title: 'Bug List',
        description: `List of all reported bugs in the system:
      • Bug ID and description
      • Reported by and submission date
      • Current status (New, In Progress, Resolved)
      • Assigned to (team member responsible)
      • Priority level (Low, Medium, High, Critical)
      • Click on any row for full details including screenshots`,
        position: 'bottom',
    },
    {
        selector: '.bug-detail-panel',
        title: 'Bug Details',
        description: `Detailed information about the selected bug:
      • Full description and steps to reproduce
      • Technical information (browser, OS, page URL)
      • Video ID and timestamp (if applicable)
      • User-provided screenshots
      • System logs from time of occurrence
      • Communication history about this issue`,
        position: 'left',
    },
    {
        selector: '.bug-status-selector',
        title: 'Update Bug Status',
        description: `Change the status of a reported bug:
      • New: Bug has been reported but not yet addressed
      • In Progress: Team is working on a resolution
      • Need Info: More information required from reporter
      • Resolved: Bug has been fixed
      • Closed: Issue has been resolved or determined not to be a bug`,
        position: 'bottom',
    },
    {
        selector: '.bug-response-form',
        title: 'Respond to Bug Report',
        description: `Communicate with the user who reported the bug:
      • Send an update on resolution progress
      • Request additional information if needed
      • Notify when the bug has been fixed
      • Suggest workarounds when available
      • Thank users for their contribution to improving the system`,
        position: 'bottom',
    },

    // System Status Dashboard
    {
        selector: '.system-status-dashboard',
        title: 'System Status',
        description: `Monitor system health and performance:
      • Server status and uptime
      • Processing queue length
      • Storage usage and limits
      • Recent errors and warnings
      • API response times
      • Video processing statistics`,
        position: 'right',
    },
    {
        selector: '.video-processing-queue',
        title: 'Video Processing Queue',
        description: `Manage videos currently being processed:
      • View all videos in the transcription/translation queue
      • Check processing status and estimated completion time
      • Prioritize specific videos in the queue
      • Pause or restart processing tasks
      • Troubleshoot failed processing jobs`,
        position: 'bottom',
    },
    {
        selector: '.system-settings-panel',
        title: 'System Settings',
        description: `Configure application-wide settings:
      • API connection parameters
      • AI transcription quality presets
      • Default video processing options
      • Email notification preferences
      • System maintenance windows
      • Log level and retention policies`,
        position: 'left',
    },
];

/**
 * User Management page help items
 */
export const userManagementHelpItems = [
    {
        selector: '.user-management-header',
        title: 'User Management',
        description: `Manage all system users and their permissions from this page:
      • View all registered users in the system
      • Create new user accounts with specific roles
      • Modify existing user permissions and language access
      • Reset passwords for users who need assistance
      • Bulk Import users from CSV file`,
        position: 'bottom',
    },
    {
        selector: '.user-list',
        title: 'User List',
        description: `View, Filter and manage all system users:
      • Search for users by name or email
      • Click on any user to view their full profile
      • Sort users by name, role, or last activity date
      • Quick indicators show access levels and active status
      • Filter By Role: Show only Admins, Editors, or Viewers
      • Filter By Status: Active or Inactive accounts
      • Filter By Language Access: Filter by language permissions
      • Filter By Last Activity: Recently active or inactive users`,
        position: 'left',
    },
    {
        selector: '.user-create-button',
        title: 'Create New User',
        description: `Add a new user to the system:
      • Enter user's email address and basic information
      • Assign role (Admin, Editor, or Viewer)
      • Set language access permissions for each language
      • Set password or generate a random secure password
      • User will receive an email with login instructions`,
        position: 'left',
    },
];

/**
 * User Edit page help items - TODO - for user edit dialog connect this
 */
export const userEditHelpItems = [
    {
        selector: '.user-profile-section',
        title: 'User Profile',
        description: `Edit basic user information:
      • Name and contact information
      • Profile picture
      • Account status (Active/Inactive)
      • Notes and administrative comments`,
        position: 'right',
    },
    {
        selector: '.user-permissions-section',
        title: 'Language Permissions',
        description: `Manage language access for this user:
      • READ: User can only view subtitles for this language
      • WRITE: User can edit subtitles for this language
      • ADMIN: User has full control over content in this language
      • Changes to permissions take effect immediately
      • Users can have different access levels for different languages`,
        position: 'bottom',
    },
    {
        selector: '.user-role-selector',
        title: 'User Role',
        description: `Set the user's system-wide role:
      • Admin: Full system access including user management
      • Editor: Can manage content but not system settings
      • Viewer: Read-only access to content
      • Role changes affect available features and permissions`,
        position: 'right',
    },
    {
        selector: '.user-password-reset',
        title: 'Reset Password',
        description: `Manage this user's password:
      • Send password reset email to the user
      • Manually set a new password
      • Generate a random secure password
      • Force password change on next login`,
        position: 'bottom',
    },
    {
        selector: '.user-activity-log',
        title: 'Activity History',
        description: `View this user's system activity:
      • Recent logins and session information
      • Content edits and changes made
      • Videos accessed and edited
      • Administrative actions performed (if applicable)`,
        position: 'left',
    },
];

/**
 * Assignment Management page help items - TODO - need to add create functionality
 */
export const assignmentManagementHelpItems = [
    {
        selector: '.assignments-management-header',
        title: 'Assignment Management',
        description: `Oversee all subtitle editing assignments in the system:
      • View all active assignments across users and languages
      • Create new assignments for specific videos and languages
      • Track progress and completion status
      • Set and adjust deadlines as needed`,
        position: 'bottom',
    },
    {
        selector: '.assignment-filters',
        title: 'Assignment Filters',
        description: `Filter the assignments view:
      • By User: See all tasks assigned to a specific user
      • By Language: View assignments for specific languages
      • By Status: Filter by Not Started, In Progress, or Completed
      • By Due Date: Find overdue assignments or upcoming deadlines
      • By Priority: Focus on high-priority tasks first`,
        position: 'bottom',
    },
    {
        selector: '.create-assignment-button', // TODO - NEED TO support this functionality
        title: 'Create Assignment',
        description: `Create a new subtitle editing assignment:
      • Select video and target language
      • Choose assignee based on language skills
      • Set processing level and due date
      • Add specific instructions for the editor`,
        position: 'left',
    },
    {
        selector: '.assignment-list',
        title: 'Assignment List',
        description: `View all current assignments:
      • Sortable list of all assigned tasks
      • Color-coded by status and priority
      • Click any row to view or edit assignment details
      • Indicators for overdue or at-risk assignments`,
        position: 'bottom',
    },
];

/**
 * Create/Edit Assignment page help items - This is for the assign dialog - TODO conneect this
 */
export const assignmentEditHelpItems = [
    {
        selector: '.assignment-details-form',
        title: 'Assignment Details',
        description: `Configure the subtitle editing assignment:
      • Video Selection: Choose which video needs subtitling
      • Language: Select target language for subtitles
      • Processing Level: Set editing level (1-3) required`,
        position: 'right',
    },
    {
        selector: '.assignment-user-selector',
        title: 'Assign To User',
        description: `Choose which user should complete this task:
      • Only users with appropriate language permissions are shown
      • System shows user workload indicator to help balance tasks
      • You can see each user's current assignment count
      • Filters help find users with specific skills`,
        position: 'bottom',
    },
    {
        selector: '.assignment-schedule-section',
        title: 'Level & Priority',
        description: `Set timing and importance for this assignment:
      • Due Date: When the task should be completed
      • Level: High/Medium/Low importance`,
        position: 'left',
    },
    {
        selector: '.assignment-instructions',
        title: 'Special Instructions',
        description: `Provide guidance for completing this assignment:
      • Add notes about special terminology
      • Mention style preferences or requirements
      • Include links to reference materials
      • Specify quality expectations`,
        position: 'bottom',
    },
];

/**
 * Bug Reports page help items - TODO Check if needed
 */
export const bugReportManagementHelpItems = [
    {
        selector: '.bug-reports-header',
        title: 'Bug Reports',
        description: `Review and manage reported issues:
      • View all user-reported problems
      • Sort by date, status, or severity
      • Assign bugs to team members for resolution
      • Track resolution progress`,
        position: 'bottom',
    },
    {
        selector: '.bug-list',
        title: 'Bug List',
        description: `List of all reported bugs in the system:
      • Bug ID and description
      • Reported by and submission date
      • Current status (New, In Progress, Resolved)
      • Assigned to (team member responsible)
      • Priority level (Low, Medium, High, Critical)`,
        position: 'bottom',
    },
    {
        selector: '.bug-filters',
        title: 'Filter Bug Reports',
        description: `Narrow down the bug list:
      • By Status: Focus on New, In Progress, or Resolved bugs
      • By Priority: Address Critical or High issues first
      • By Reporter: View bugs from specific users
      • By Date Range: Recent reports or historical issues`,
        position: 'right',
    },
    {
        selector: '.assign-bug-button',
        title: 'Assign Bug',
        description: `Delegate bug resolution responsibility:
      • Choose team member with appropriate skills
      • Set priority and target resolution date
      • Add notes with troubleshooting suggestions
      • Notify assignee automatically by email`,
        position: 'left',
    },
];

/**
 * Bug Detail page help items - - TODO Check if needed
 */
export const bugDetailHelpItems = [
    {
        selector: '.bug-detail-panel',
        title: 'Bug Details',
        description: `Detailed information about the selected bug:
      • Full description and steps to reproduce
      • Technical information (browser, OS, page URL)
      • Video ID and timestamp (if applicable)
      • User-provided screenshots
      • System logs from time of occurrence`,
        position: 'right',
    },
    {
        selector: '.bug-status-selector',
        title: 'Update Bug Status',
        description: `Change the status of this reported bug:
      • New: Bug has been reported but not yet addressed
      • In Progress: Team is working on a resolution
      • Need Info: More information required from reporter
      • Resolved: Bug has been fixed
      • Closed: Issue has been resolved or determined not to be a bug`,
        position: 'bottom',
    },
    {
        selector: '.bug-response-form',
        title: 'Respond to Bug Report',
        description: `Communicate with the user who reported the bug:
      • Send an update on resolution progress
      • Request additional information if needed
      • Notify when the bug has been fixed
      • Suggest workarounds when available`,
        position: 'bottom',
    },
    {
        selector: '.bug-history-log',
        title: 'Resolution History',
        description: `Track the bug resolution process:
      • Timeline of status changes and updates
      • Communication history with the reporter
      • Notes from team members working on the issue
      • Related system changes or fixes applied`,
        position: 'left',
    },
];

/**
 * System Status page help items- TODO Check if needed
 */
export const systemStatusHelpItems = [
    {
        selector: '.system-status-dashboard',
        title: 'System Status Overview',
        description: `Monitor overall system health:
      • Server status and uptime
      • Processing queue length
      • Storage usage and limits
      • Recent errors and warnings
      • API response times`,
        position: 'bottom',
    },
    {
        selector: '.video-processing-queue',
        title: 'Video Processing Queue',
        description: `Manage videos currently being processed:
      • View all videos in the transcription/translation queue
      • Check processing status and estimated completion time
      • Prioritize specific videos in the queue
      • Pause or restart processing tasks`,
        position: 'right',
    },
    {
        selector: '.system-metrics-graphs',
        title: 'Performance Metrics',
        description: `View system performance over time:
      • CPU and memory usage trends
      • API response time history
      • Storage space utilization
      • User activity levels
      • Video processing throughput`,
        position: 'bottom',
    },
    {
        selector: '.system-alerts',
        title: 'System Alerts',
        description: `Critical notifications about system issues:
      • Error rate warnings
      • Resource limitation alerts
      • Security notifications
      • Maintenance reminders
      • Click any alert for detailed information`,
        position: 'left',
    },
];

/**
 * System Settings page help items - TODO Check if needed
 */
export const systemSettingsHelpItems = [
    {
        selector: '.system-settings-panel',
        title: 'System Settings',
        description: `Configure application-wide settings:
      • API connection parameters
      • AI transcription quality presets
      • Default video processing options
      • Email notification preferences
      • System maintenance windows`,
        position: 'bottom',
    },
    {
        selector: '.transcription-settings',
        title: 'Transcription Settings',
        description: `Configure AI transcription behavior:
      • Default language detection
      • Speech recognition quality vs. speed balance
      • Speaker diarization settings
      • Minimum confidence thresholds
      • Special terminology dictionaries`,
        position: 'right',
    },
    {
        selector: '.notification-settings',
        title: 'Notification Settings',
        description: `Manage system-wide notifications:
      • Email templates for different notification types
      • Default notification frequency
      • User notification preferences
      • Urgent alert thresholds
      • Notification delivery methods`,
        position: 'left',
    },
    {
        selector: '.backup-restore-section',
        title: 'Backup & Restore',
        description: `Manage system data protection:
      • Configure automatic backup schedule
      • Perform manual backups
      • Access backup history
      • Restore data from previous backups
      • Download backups for offline storage`,
        position: 'bottom',
    },
];

/**
 * Active Sessions page help items - TODO Check if needed
 */
export const activeSessionsHelpItems = [
    {
        selector: '.session-tabs',
        title: 'Session Types',
        description: `Switch between different session views:
      • My Sessions: View only your active editing sessions
      • All Users' Sessions: (Admin only) View active sessions across all users
      Click the appropriate tab to switch views.`,
        position: 'bottom',
    },
    {
        selector: '.sessions-list',
        title: 'Active Sessions',
        description: `View all currently active subtitle editing sessions:
      • See which videos and languages are being edited
      • Check when sessions were started and last active
      • Monitor total session duration
      • End sessions that are no longer needed`,
        position: 'bottom',
    },
    {
        selector: '.refresh-button',
        title: 'Refresh Sessions',
        description: 'Update the active sessions list to see the latest status.',
        position: 'left',
    },
];

/**
 * Video processing status help items
 */
export const videoProcessingHelpItems = [
    {
        selector: '.processing-filters',
        title: 'Filter Options',
        description: `Refine the videos displayed:
      • Time Range: Last 24 hours, 7 days, or 30 days
      • Status: All, In Progress, Completed, or Failed
      • Search: Filter by video name or ID`,
        position: 'bottom',
    },
    {
        selector: '.processing-status-summary',
        title: 'Processing Summary',
        description: `Overview of video processing metrics:
      • Current queue size and completion rate
      • Average processing times for different stages
      • Success and failure statistics
      These stats help monitor system performance.`,
        position: 'bottom',
    },
    {
        selector: '.video-processing-queue',
        title: 'Processing Queue',
        description: `List of videos being processed:
      • View processing status and progress for each video
      • See detailed timeline of processing steps
      • Identify bottlenecks or failures in the pipeline
      • View estimated completion times`,
        position: 'bottom',
    },
];

/**
 * MongoDB Files Browser help items
 */
export const mongoFilesBrowserHelpItems = [
    {
        selector: '.breadcrumbs-navigation',
        title: 'Folder Navigation',
        description: `Navigate through the MongoDB file structure:
      • Click folder names to move inside folders
      • Use breadcrumbs to quickly navigate up the hierarchy
      • View current path at the top of the page`,
        position: 'bottom',
    },
    {
        selector: '.files-list',
        title: 'Files List',
        description: `View files stored in the database:
      • See file metadata including IDs and creation dates
      • Identify file types and sizes
      • View hierarchical folder structure
      • Manage file organization`,
        position: 'top',
    },

    {
        selector: '.back-to-dashboard',
        ...commonHelpItems.backtoDashboard,
    },
];

/**
 * Admin dashboard help items
 */
export const adminDashboardHelpItems = [
    {
        selector: '.team-management-section',
        title: 'Team Management',
        description: `Tools for managing your team and organization:
      • User Management: Create and modify user accounts
      • Assignment Management: Track work allocated to team members
      • Bug Reports: View and address reported issues`,
        position: 'bottom',
    },
    {
        selector: '.admin-tools-section',
        title: 'Administrative Tools',
        description: `System-level administrative features:
      • Files & Folders: Manage content organization
      • Videos: Manage videos directly
      • Data Migration: Transfer data between systems`,
        position: 'bottom',
    },
    {
        selector: '.user-management-card',
        title: 'User Management',
        description: `Create and manage user accounts:
      • Add new team members with specific roles
      • Set language access permissions
      • Reset passwords and manage account status`,
        position: 'right',
    },
    {
        selector: '.assignment-management-card',
        title: 'Assignment Management',
        description: `Track and manage work assignments:
      • Assign subtitle editing tasks to team members
      • Set deadlines and monitor progress
      • View completed and outstanding work`,
        position: 'right',
    },
    {
        selector: '.bug-reports-card',
        title: 'Bug Reports',
        description: `Handle reported application issues:
      • Review bug reports submitted by users
      • Track resolution status and progress
      • Communicate with users about fixes`,
        position: 'right',
    },
];

/**
 * Trash Bin help items
 */
export const trashBinHelpItems = [
    {
        selector: '.trash-bin-header',
        title: 'Recently Deleted Items',
        description: `View items that have been deleted within the last 30 days:
      • Items are automatically removed after 30 days
      • Restore items to their original or a new location
      • Permanently delete items before the 30-day period`,
        position: 'bottom',
    },
    {
        selector: '.trash-table',
        title: 'Deleted Items List',
        description: `Manage your deleted items:
      • See when and by whom items were deleted
      • View the original location of each item
      • Restore folders to their original or a new location
      • Permanently remove items with the delete action`,
        position: 'top',
    },
    {
        selector: '.refresh-button',
        title: 'Refresh',
        description: 'Update the list to see the latest deleted items.',
        position: 'left',
    },
];

// ─── Editor Help Items ────────────────────────────────────────────────────────

export const subtitleEditorHelpItems = [
    {
        selector: '.toolbar-container',
        title: 'Subtitle Editor Toolbar',
        description: `This toolbar contains all editing controls:
      • Save (Ctrl+S) — saves your current subtitle edits
      • Undo (Ctrl+Z) — reverts the last change
      • Find & Replace (Ctrl+F) — search and replace text across all subtitles
      • Keyboard Shortcuts (Ctrl+/) — view all available shortcuts
      • Validation Badge — shows errors like overlapping timecodes or invalid durations`,
        position: 'bottom',
    },
    {
        selector: '#subtitle-editor-video-panel',
        title: 'Video Preview',
        description: `Watch the video in sync with your subtitles:
      • Click anywhere on the video to play/pause (Space key also works)
      • Subtitles highlight in the list as they appear in the video
      • Use the Follow Playhead toggle to auto-scroll the subtitle list during playback
      • Speed control lets you slow down for careful editing`,
        position: 'right',
    },
    {
        selector: '#subtitle-editor-subtitle-panel',
        title: 'Subtitle List',
        description: `Each row is one subtitle cue:
      • Click any cue to select it — the video jumps to that timecode
      • Edit text directly in the text field
      • Use the nudge buttons (Ctrl+← / Ctrl+→) to shift timecodes
      • Press Shift+Enter to insert a new cue below the current one
      • Press Ctrl+M to merge a cue with the next one`,
        position: 'left',
    },
    {
        selector: '#subtitle-editor-timeline-panel',
        title: 'Timeline',
        description: `Visual timeline of all subtitle cues:
      • Drag cue blocks to reposition them in time
      • Drag the left or right edge of a block to adjust its start or end time
      • Ctrl+Scroll to zoom in/out for precise timing
      • The vertical line shows the current playback position`,
        position: 'top',
    },
];

export const videoEditorHelpItems = [
    {
        selector: '#video-editor-topbar',
        title: 'Video Editor Toolbar',
        description: `Controls at the top of the editor:
      • Breadcrumbs (left) — navigate back to the folder or file
      • Media button — opens the Media Library to add background music or a logo overlay
      • Export button — render and download the final video with cuts, music, and subtitles applied`,
        position: 'bottom',
    },
    {
        selector: '#video-editor-monitor',
        title: 'Program Monitor',
        description: `Preview the video with all edits applied:
      • Subtitles are overlaid at the timecode-accurate position
      • Cut markers are shown as red lines on the timeline — clips between markers will be removed
      • Use the transport controls (play, seek) to review your edits`,
        position: 'right',
    },
    {
        selector: '#video-editor-timeline',
        title: 'Edit Timeline',
        description: `The timeline shows all tracks for the video:
      • Video Track — the source video with cut markers; click to add a cut at any point
      • Subtitle Track — shows subtitle cues for reference
      • Music Track — drag and position background music clips
      • Logo Track — add a watermark/logo at a specific time range
      • Drag the playhead to scrub through the video`,
        position: 'top',
    },
];

export const reelEditorHelpItems = [
    {
        selector: '#reel-editor-canvas',
        title: 'Reel Preview Canvas',
        description: `Live preview of the reel with all styling applied:
      • The canvas shows the video in the selected platform format (Instagram, TikTok, YouTube, etc.)
      • Subtitle and overlay positions update in real time as you adjust settings
      • Click play to preview the reel with audio and subtitles`,
        position: 'right',
    },
    {
        selector: '#reel-editor-platform',
        title: 'Platform Presets',
        description: `Choose the target platform to auto-set the correct aspect ratio:
      • Instagram Reels / TikTok — 9:16 vertical (1080×1920)
      • YouTube — 16:9 landscape (1920×1080)
      • Instagram Square — 1:1 (1080×1080)
      The canvas resizes automatically when you switch platforms.`,
        position: 'bottom',
    },
    {
        selector: '#reel-editor-subtitles',
        title: 'Subtitle Styling',
        description: `Customize how subtitles appear on the reel:
      • Font, size, color, and background are all adjustable
      • Position subtitles anywhere on the canvas by dragging
      • Word-specific highlight colors help emphasize key words
      • Changes preview instantly in the canvas`,
        position: 'left',
    },
    {
        selector: '#reel-editor-export',
        title: 'Export Reel',
        description: `Render and download the final reel:
      • The exported file includes the video, audio, and burned-in subtitles
      • Output is in the selected platform's preferred format
      • Processing happens on the server — you'll be notified when it's ready`,
        position: 'left',
    },
];

export const textEditorHelpItems = [
    {
        selector: '#text-editor-appbar',
        title: 'Text Editor Toolbar',
        description: `Main controls for managing the text file:
      • Save — saves your edits (also auto-saves periodically)
      • Undo — reverts the last change
      • Language tabs — switch between source and translated language views
      • Translate button — run AI translation into all configured target languages
      • Download — export the current language version as a text or PDF file`,
        position: 'bottom',
    },
    {
        selector: '#text-editor-formatting',
        title: 'Formatting Toolbar',
        description: `Rich text formatting options for the document:
      • Bold, Italic, Underline — standard text formatting (Ctrl+B, Ctrl+I, Ctrl+U)
      • Bullet list / Numbered list — organize content into lists
      • Clear formatting — remove all inline styles from selected text
      These apply to the currently active language tab.`,
        position: 'bottom',
    },
    {
        selector: '#text-editor-content',
        title: 'Text Content Area',
        description: `Edit the text content directly here:
      • Click anywhere to position your cursor and type
      • Content is shared in the source language; translated versions are generated by AI
      • HTML/rich text pasted from other sources is preserved with formatting`,
        position: 'right',
    },
    {
        selector: '#text-editor-language-tabs',
        title: 'Language Tabs',
        description: `Switch between source and translated versions of the document:
      • The first tab is always the source language you selected when creating the file
      • Other tabs show translated content — each language is saved separately
      • You can manually edit any language's content, not just the source`,
        position: 'bottom',
    },
];

// ─── Dialog Help Items ────────────────────────────────────────────────────────

export const uploadVideoDialogHelpItems = [
    {
        selector: '#upload-video-source',
        title: 'Video Source',
        description: `Choose how to provide the video:
      • Google Drive — paste a shared link (must be publicly accessible) and click Check Access to verify
      • YouTube — paste a YouTube URL (coming soon)
      • Local File — upload directly from your computer`,
        position: 'right',
    },
    {
        selector: '#upload-language-selection',
        title: 'Language Selection',
        description: `Select which languages AI should generate subtitles for:
      • Basic languages (English, Hindi, Gujarati) appear first for quick access
      • Add more languages from the dropdown as needed
      • Each selected language creates a separate subtitle track in the editor`,
        position: 'right',
    },
    {
        selector: '#upload-text2text-options',
        title: 'Text2Text Options',
        description: `Upload an existing English SRT or VTT file to auto-translate into other Western languages:
      • Use this when you already have English subtitles and want translations without re-running AI speech recognition
      • Gujarati and Hindi translations should be done via the Text2Text dialog instead`,
        position: 'right',
    },
    {
        selector: '#upload-advanced-options',
        title: 'Advanced Options',
        description: `Special modes for subtitle generation:
      • Generate Empty SRT — creates blank subtitle files without running AI; useful when you'll type subtitles manually
      • Only Generate Gujarati SRT — runs AI only for Gujarati and creates a blank English SRT; use for Gujarati-language videos`,
        position: 'top',
    },
];

export const splitVideoDialogHelpItems = [
    {
        selector: '#split-number-of-parts',
        title: 'Number of Parts',
        description: `The video will be divided into this many equal-duration segments:
      • Each part becomes a separate video entry in the destination folder
      • Minimum: 2 parts, Maximum: 50 parts
      • Choose based on the natural logical sections of the video`,
        position: 'right',
    },
    {
        selector: '#split-video-source',
        title: 'Video Source',
        description: `Choose how to provide the video file:
      • Google Drive — paste a publicly shared link and click Check Access to verify
      • Local File — upload a video directly from your computer`,
        position: 'right',
    },
    {
        selector: '#split-subtitle-mode',
        title: 'Subtitle Mode',
        description: `Controls how subtitles are created for each split part:
      • AI-generated — sends each part to speech-to-text AI (recommended, takes longer)
      • Skip subtitles — creates blank subtitle files; you can fill them in the editor later
      • Upload SRT — splits your provided SRT file by timestamp and assigns segments to each part`,
        position: 'right',
    },
];

export const createTextFileDialogHelpItems = [
    {
        selector: '#create-auto-translate',
        title: 'Auto Translate',
        description: `When checked, AI will automatically translate the text into all selected target languages as soon as the file is uploaded:
      • Leave unchecked to upload the file first and run translation later from the Text Editor
      • Translation runs in the background — you can continue working while it processes`,
        position: 'right',
    },
    {
        selector: '#source-language',
        title: 'Source Language',
        description: `The language the text content is written in:
      • This tells the system what language to treat as the original source
      • For Gujarati devotional content, choose Gujarati
      • The source language is automatically excluded from the target languages list`,
        position: 'right',
    },
    {
        selector: '#create-target-languages',
        title: 'Target Languages',
        description: `Languages the text will be translated into:
      • Select one or more languages — each will get its own translated version of the file
      • The source language is automatically excluded from this list
      • You can add or remove target languages later from the Text Editor`,
        position: 'right',
    },
];

export const progressStagesDialogHelpItems = [
    {
        selector: '#progress-stage-progression',
        title: 'Stage Progression',
        description: `Stages track how far the editing work has progressed for each language:
      • L0 - AI Complete — AI has generated raw subtitles, ready for first-pass editing
      • L1 - Complete — First sevak review done (basic edits applied)
      • L2 - Complete — Second review done (quality check complete)
      • L3 - Complete — Final review done, approved for publishing
      • Published — Released and visible to end users
      Stages advance in order; reverting to earlier stages is allowed if corrections are needed.`,
        position: 'bottom',
    },
    {
        selector: '#progress-language-grid',
        title: 'Language Stage Grid',
        description: `Each language row shows its current stage and lets you change it:
      • Click a stage button for a language to update its progress
      • Only one stage can be active per language at a time
      • Admins can move any language to any stage; editors can only advance stages they have write access to`,
        position: 'left',
    },
];

export const dubbingTransformDialogHelpItems = [
    {
        selector: '#dubbing-audio-source',
        title: 'Audio Input Source',
        description: `Choose where the dubbed audio comes from:
      • Google Drive — paste a shared link to an audio file (MP3, WAV, etc.)
      • Local File — upload an audio file directly from your computer
      Only one source is needed — Google Drive and local file are interchangeable, use whichever is convenient.`,
        position: 'right',
    },
    {
        selector: '#dubbing-sharepoint',
        title: 'SharePoint Output',
        description: `Optionally upload the processed audio back to SharePoint when complete:
      • Enter the SharePoint Base URL and Target Folder path
      • Enable the toggle to auto-upload after processing finishes
      • Leave the toggle off if you only need the file available within the app`,
        position: 'right',
    },
    {
        selector: '#dubbing-source-language',
        title: 'Source Language',
        description: `The language spoken in the audio file you are providing:
      • This tells the system which subtitle track to align the audio with
      • Make sure this matches the actual language of your audio recording`,
        position: 'right',
    },
    {
        selector: '#dubbing-voice-mixing',
        title: 'Voice Mixing Rate',
        description: `Controls how much of the synthesized dubbed voice vs the original audio background is retained:
      • 0% — fully synthetic voice only
      • 100% — fully original audio only
      • Recommended: 20–40% for a natural blend where the voice is clear but ambient sound is preserved`,
        position: 'top',
    },
];
